import { v4 as uuidv4 } from "uuid"
import { asyncAction } from "../common"
import { getState } from "storeAccess"
import { limsRequest } from "./limsRequest"
import * as samples from "../samples"

// OOS / OOT register + two-phase INVESTIGATION (F-D1, PRD-005 Area 4 / SPEC-018; SPEC-021
// cutover: persisted via the REAL /lims/oos endpoints). An Out Of Specification (OOS) or Out Of
// Trend (OOT) event raised against a sample analysis, carried through a formal two-phase
// investigation (QC guide ch. 8).
//
// Lifecycle (server-enforced): open -> investigating -> qa-review -> closed.
//
// BE SPEC-072 makes it one straight line: the lab works Phase 1 and leaves it ONLY through the
// signed hand-off (`handOff` — complete Phase 1 required), which itself opens Phase 2 when it is
// warranted. From qa-review on only QA works the case (Phase 2, close, decide) and Phase 1 is
// read-only. QA can also OPEN a case, signed, on a result it holds — the only way QA stops a lot.
//
// The SERVER owns the state machine, the audit trail and every signature point (SP-E Phase 4.3):
//   - Content saves (phase1/phase2 edits, CAPAs) PATCH only their data — they NEVER send `state`
//     (SPEC-021 M1: the backend dispatches on the state field and would misroute a qa-review-state
//     save to escalate → 409). The backend itself advances open→investigating on Phase-1 work.
//   - A NEWLY-applied phase signature requires the signer's `password` (+ optional `reason`) at
//     the request-body TOP LEVEL: technician → "analyst" capability; supervisor → "reviewer"
//     (4-eyes ≠ technician); phase-2 → "qa". The server replaces the captured FE signature
//     blob with an immutable server signature reference.
//   - close → "qa" capability, 4-eyes ≠ raiser/technician/supervisor/phase-2 signers, behind the
//     server-side completeness gate (Phase 1 fully checked + concluded + both signatures; Phase-2
//     outcome when escalated) which 422s with precise messages the UI surfaces verbatim.
//
// QA dispositions with batch impact go through the ATOMIC POST /lims/qaDecisions instead of this
// close endpoint (one QA signature = decision + OOS close + batch hold) — see limsQaDecisions.
//
// Data shape: see docs/specs/lims-api-contract.md (phase1/phase2/capas ride as JSONB blobs in the
// FE camelCase shape; phase signatures become server references {signatureId, memberId, initials,
// reason, timestamp} once hardened).
const initialState = { items: [], isLoading: false }

// The canonical six Phase-1 checklist points (QC guide ch. 8). The KEY is stable (it is what is
// persisted on the record + audited); the LABEL is an i18n key resolved in the UI. Seeded onto a
// new investigation so the checklist is always complete and consistent.
export const PHASE1_CHECK_KEYS = [
  "calculations",
  "weighings",
  "standard",
  "system",
  "reinjection",
  "conclusion",
]

// Phase-2 outcomes (a product-investigation disposition for the lot).
export const PHASE2_OUTCOME = {
  LOT_APPROVED: "lot-approved",
  LOT_REJECTED: "lot-rejected",
  RECALLED: "recalled",
}

// CAPA classification + workflow status.
export const CAPA_TYPE = { CORRECTIVE: "corrective", PREVENTIVE: "preventive" }
export const CAPA_STATUS = { OPEN: "open", IN_PROGRESS: "in-progress", CLOSED: "closed" }

// Both CAPA mutations (add + update) dispatch the SAME reducer action — a CAPA change is a CAPA
// change as far as the store is concerned — so the action type lives in one constant.
const CAPA_ACTION = "limsOos/capa"

// A fresh, fully-populated Phase-1 record (empty checklist notes, nothing found yet).
function newPhase1() {
  return {
    checklist: PHASE1_CHECK_KEYS.map((key) => ({ key, checked: false, note: "" })),
    assignableCauseFound: false,
    conclusion: "",
    technicianSignature: null,
    supervisorSignature: null,
  }
}

// Newest first: an open register is most useful with the freshest events at the top.
function sortByRaisedDesc(items) {
  return [...items].sort((a, b) => ((a.raisedAt || "") < (b.raisedAt || "") ? 1 : -1))
}

// Strip the in-memory password off an ESignatureModal payload before it is embedded in a phase
// blob, and lift it (plus the reason) onto the request-body top level (SPEC-021 B1). Returns
// { signature, password, reason } — signature null when none was applied.
function liftSigningFields(signature) {
  if (!signature) return { signature: null, password: undefined, reason: undefined }
  const { password, ...rest } = signature
  return { signature: rest, password: password || undefined, reason: rest.reason || undefined }
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsOos/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsOos/fetch":
      return { items: sortByRaisedDesc(action.res), isLoading: false }
    case "limsOos/open":
      return { ...state, items: sortByRaisedDesc([...state.items.filter((x) => x.id !== action.res.id), action.res]) }
    case "limsOos/savePhase1":
    case "limsOos/handOff":
    case "limsOos/savePhase2":
    case CAPA_ACTION:
    case "limsOos/close":
    case "limsOos/reopen":
      return { ...state, items: sortByRaisedDesc(state.items.map((x) => (x.id === action.res.id ? action.res : x))) }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsOos/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/oos").then((res) => res.items)
})

// SPEC-018: OPEN a formal OOS investigation MANUALLY from a FAIL result (analyst/reviewer). This
// is the ONLY way an OOS record is created (SPEC-020 C5 removed the lightweight flag-path raise()
// register entries). Seeding `phase1` makes the backend treat the POST as openInvestigation (it
// completes the checklist, stamps raisedBy/raisedAt server-side, FLAGS the sample and writes the
// audit entry). POST is an upsert (a provided id replaces), so re-opening for the same
// sample/analysis is idempotent when the caller passes a stable id.
//
// BE SPEC-072 R2: QA opens one too, on a result it holds (passed or failed), by passing its
// e-`signature` ({password, reason?}). The password then rides top-level and the server signs the
// open with the "qa" capability; the case is always an OOS and goes to the lab for Phase 1.
export const openInvestigation = asyncAction(
  "limsOos/open",
  (_dispatch, { id, sampleId, analysisId, sampleLabel, analysisLabel, type, reason, signature } = {}) => {
    const input = {
      id: id || undefined,
      sampleId: sampleId || null,
      analysisId: analysisId || null,
      sampleLabel: sampleLabel || "",
      analysisLabel: analysisLabel || "",
      // SPEC-037 (F5): forward the caller's investigation type so an OOT-marked row opens a real OOT
      // record (lab-only, Phase-2 barred) — not a generic OOS. The backend persists "OOT" only for
      // an explicit "OOT" and otherwise defaults to "OOS" (routes/lims/oos.py); mirror that here.
      type: type === "OOT" ? "OOT" : "OOS",
      reason: reason || null,
      phase1: newPhase1(),
    }
    if (signature) {
      const sig = liftSigningFields(signature)
      return limsRequest("POST", "/lims/oos", {
        input: { ...input, type: "OOS", reason: sig.reason || null },
        password: sig.password,
        reason: sig.reason || null,
      }).then((res) => res.item)
    }
    return limsRequest("POST", "/lims/oos", { input }).then((res) => res.item)
  }
)

// SPEC-018 Phase 1 (lab investigation): persist the 6-point checklist + assignable-cause finding +
// conclusion + the technician/supervisor signatures. `data` is the partial phase1 patch; it is
// merged onto the existing phase1 (or a fresh one) so a partial save is lossless. The patch sends
// ONLY `phase1` — never `state` (SPEC-021 M1); the backend advances open→investigating itself and
// never regresses qa-review/closed. A NEWLY-applied technician/supervisor signature carries the
// signer's password top-level so the server can harden it into a real e-signature.
export const savePhase1 = asyncAction("limsOos/savePhase1", (_dispatch, id, data = {}) => {
  const current = selectOos(getState(), id)
  const base = (current && current.phase1) || newPhase1()
  // Lift the password off whichever signature is being newly applied in this save (at most one
  // per save in the UI flow); the scrubbed signature blob is what lands in the phase data.
  const tech = liftSigningFields(data.technicianSignature)
  const sup = liftSigningFields(data.supervisorSignature)
  const phase1 = {
    ...base,
    ...data,
    ...(data.technicianSignature !== undefined ? { technicianSignature: tech.signature } : {}),
    ...(data.supervisorSignature !== undefined ? { supervisorSignature: sup.signature } : {}),
  }
  return limsRequest("PATCH", `/lims/oos/${id}`, {
    patch: { phase1 },
    password: sup.password || tech.password,
    reason: sup.reason || tech.reason,
  }).then((res) => res.item)
})

// BE SPEC-072 R3/R4: the lab's ONE hand-off out of Phase 1 (investigating -> qa-review). A
// reviewer e-signed act (reviewer capability, 4-eyes != raiser) that the server only accepts on a
// complete Phase 1 (checklist, conclusion, both signatures — 422 with the precise list otherwise).
// The SERVER decides what follows: it opens Phase 2 when the case is not an OOT and Phase 1 found
// no assignable cause, and otherwise sends the case to QA for closing — `phase2Warranted` below
// mirrors that only to label the button. The patch sends ONLY the state.
export const handOff = asyncAction("limsOos/handOff", (_dispatch, id, signature) => {
  const sig = liftSigningFields(signature)
  return limsRequest("PATCH", `/lims/oos/${id}`, {
    patch: { state: "qa-review" },
    password: sig.password,
    reason: sig.reason,
  }).then((res) => res.item)
})

// Whether the hand-off of `oos` opens Phase 2 (mirror of the backend's `_phase2_warranted`).
export function phase2Warranted(oos) {
  return Boolean(oos) && oos.type !== "OOT" && !(oos.phase1 && oos.phase1.assignableCauseFound)
}

// The Phase-1 items still missing before the hand-off (mirror of the backend's
// `_phase1_completeness_errors`), as i18n keys; empty when Phase 1 is complete. The same items gate
// the close, so the close-gate keys are reused.
export function phase1MissingKeys(phase1) {
  const missing = []
  const checklist = Array.isArray(phase1 && phase1.checklist) ? phase1.checklist : []
  if (checklist.length === 0 || !checklist.every((item) => item && item.checked)) {
    missing.push("limsOosCloseMissingChecklist")
  }
  if (!String((phase1 && phase1.conclusion) || "").trim()) missing.push("limsOosCloseMissingConclusion")
  if (!(phase1 && phase1.technicianSignature)) missing.push("limsOosCloseMissingTechSig")
  if (!(phase1 && phase1.supervisorSignature)) missing.push("limsOosCloseMissingSupSig")
  return missing
}

// SPEC-018 Phase 2 (product investigation): persist resampling / production-record review /
// stability assessment + the outcome (lot-approved | lot-rejected | recalled) + a signature.
// `data` is merged onto the existing phase2 so a partial save is lossless; a NEWLY-applied
// signature carries the signer's password top-level (server hardens it; "qa" capability). Only
// accepted once the case is handed to QA (BE SPEC-072 R5).
export const savePhase2 = asyncAction("limsOos/savePhase2", (_dispatch, id, data = {}) => {
  const current = selectOos(getState(), id)
  const base = (current && current.phase2) || {
    resampling: "",
    productionReview: "",
    stabilityAssessment: "",
    outcome: null,
    signature: null,
  }
  const sig = liftSigningFields(data.signature)
  const phase2 = {
    ...base,
    ...data,
    ...(data.signature !== undefined ? { signature: sig.signature } : {}),
  }
  return limsRequest("PATCH", `/lims/oos/${id}`, {
    patch: { phase2 },
    password: sig.password,
    reason: sig.reason,
  }).then((res) => res.item)
})

// SPEC-018: add a CAPA (corrective/preventive action) embedded in the investigation. `capa` carries
// { type, description, owner, dueDate, status }; a fresh id + a default OPEN status are filled in.
// The capas array is appended (immutably) and PATCHed back (no e-signature; server audits).
export const addCapa = asyncAction(CAPA_ACTION, (_dispatch, id, capa = {}) => {
  const current = selectOos(getState(), id)
  const entry = {
    id: uuidv4(),
    type: capa.type === CAPA_TYPE.PREVENTIVE ? CAPA_TYPE.PREVENTIVE : CAPA_TYPE.CORRECTIVE,
    description: capa.description || "",
    owner: capa.owner || "",
    dueDate: capa.dueDate || null,
    status: capa.status || CAPA_STATUS.OPEN,
  }
  const capas = [...((current && current.capas) || []), entry]
  return limsRequest("PATCH", `/lims/oos/${id}`, { patch: { capas } }).then((res) => res.item)
})

// SPEC-018: update an existing CAPA (e.g. change status open -> in-progress -> closed, or edit the
// owner / due date). `patch` is merged onto the matching CAPA by id.
export const updateCapa = asyncAction(CAPA_ACTION, (_dispatch, id, capaId, patch = {}) => {
  const current = selectOos(getState(), id)
  const capas = ((current && current.capas) || []).map((c) => (c.id === capaId ? { ...c, ...patch } : c))
  return limsRequest("PATCH", `/lims/oos/${id}`, { patch: { capas } }).then((res) => res.item)
})

// Close an OOS WITHOUT a batch-impacting disposition (a disposition that creates a rerun or a
// batch hold goes through the ATOMIC POST /lims/qaDecisions instead — see limsQaDecisions.decide).
// The QA `signature`'s password rides top-level; the server enforces the "qa" capability, 4-eyes
// (≠ raiser / technician / supervisor / phase-2 signers) and the close-completeness gate (422 with
// precise messages — surfaced verbatim by the modal), then stamps closedBy/closedAt/closeReason +
// disposition + qaDecisionId and unflags the sample when no other open OOS remains.
export const close = asyncAction(
  "limsOos/close",
  (dispatch, id, { reason, qaDecisionId, signature, disposition } = {}) => {
    const sig = liftSigningFields(signature)
    return limsRequest("PATCH", `/lims/oos/${id}`, {
      patch: {
        state: "closed",
        qaDecisionId: qaDecisionId || null,
        disposition: disposition || null,
      },
      password: sig.password,
      reason: sig.reason || reason || undefined,
    }).then((res) => {
      const item = res.item
      // The backend already unflagged the sample (when this was the last open OOS); mirror that
      // in the FE samples/sampleView state through the existing unflag action so the Sample View
      // reflects it without a full refetch. Idempotent against the server's own unflag.
      if (item && item.sampleId) {
        const otherOpen = selectOpenInvestigationsForSample(getState(), item.sampleId).filter(
          (o) => o.id !== id
        )
        if (otherOpen.length === 0) {
          dispatch(samples.unflagSample(item.sampleId, item.sampleLabel || ""))
        }
      }
      return item
    })
  }
)

// PRD-006 K13 (c) — REOPEN a CLOSED OOS investigation via a signed, reason-mandatory QA action.
// Only valid from `closed` (server 409s otherwise). The server moves the state back to
// `investigating`, NULLs the live closed fields (closedBy/closedAt/closeReason read back null),
// appends a `reopenHistory` entry, RE-FLAGS the sample and re-blocks the release gate — the original
// close signature + audit are preserved. The QA password rides top-level (qa capability, 4-eyes ≠
// the closedBy signer); the reason is MANDATORY (server 422s on blank — the ESignatureModal mirrors).
// After success the caller refetches the OOS register + samples so the re-flag mirror lands.
export const reopen = asyncAction("limsOos/reopen", (dispatch, id, signature = null) => {
  const sig = liftSigningFields(signature)
  return limsRequest("PATCH", `/lims/oos/${id}`, {
    patch: { state: "investigating" },
    password: sig.password,
    reason: sig.reason,
  }).then((res) => {
    const item = res.item
    // The backend re-flagged the sample on reopen; mirror it in the FE samples/sampleView state via
    // the existing flag action (idempotent against the server's own re-flag) so the Sample View
    // shows the block again without a full refetch.
    if (item && item.sampleId) {
      dispatch(samples.flagSample(item.sampleId, item.sampleLabel || ""))
    }
    return item
  })
})

export const selectAllOos = (state) => state.limsOos.items

// One OOS record by id (or undefined). Used by the phase/CAPA thunks to merge partial patches.
export const selectOos = (state, id) => state.limsOos.items.find((x) => x.id === id)

// Open OOS = anything not yet closed (open OR investigating). This is what QA release
// (F-C4) will consume to block a release while an OOS is still open.
export const selectOpenOos = (state) => state.limsOos.items.filter((x) => x.state !== "closed")

// Open OOS for a specific sample — F-C4 can use this to block that one sample's release.
export const selectOpenOosForSample = (state, sampleId) =>
  state.limsOos.items.filter((x) => x.state !== "closed" && x.sampleId === sampleId)

// Every OOS for a sample regardless of state — the Sample View uses this to decide whether to
// surface a "create rerun" affordance / a batch-reject STOP icon against a sample's analyses.
export const selectOosForSample = (state, sampleId) =>
  state.limsOos.items.filter((x) => x.sampleId === sampleId)

// SPEC-018: OPEN INVESTIGATIONS for a specific sample — a not-yet-closed OOS that carries the
// structured Phase-1 record (i.e. was opened as a formal investigation, not merely raised as a
// register entry). The release gate consults this to put the sample's lot ON_HOLD (recoverable)
// until the investigation is closed with a QA signature.
export const selectOpenInvestigationsForSample = (state, sampleId) =>
  state.limsOos.items.filter((x) => x.sampleId === sampleId && x.state !== "closed" && !!x.phase1)

// SPEC-018 worklist: every open OOS INVESTIGATION across all samples (not-yet-closed + carrying a
// Phase-1 record), newest first. SPEC-022 K10 moved the team-facing list of these to the dedicated
// "Investigations" menu screen (Phase 1 is lab work) — the team's entry to in-flight investigations.
export const selectOpenInvestigations = (state) =>
  state.limsOos.items.filter((x) => x.state !== "closed" && !!x.phase1)

// SPEC-037 (F5): open OOT investigations only (type "OOT", not-yet-closed, carrying a Phase-1
// record). A convenience filter over the same open-investigation set for any OOT-specific surface.
export const selectOpenOotInvestigations = (state) =>
  state.limsOos.items.filter((x) => x.state !== "closed" && !!x.phase1 && x.type === "OOT")

// PRD-006 K13 (c): CLOSED OOS investigations (carrying a Phase-1 record), newest first. The
// Investigations screen's "show closed" toggle reads this so QA can REOPEN a wrongly-closed
// investigation as a signed correction.
export const selectClosedInvestigations = (state) =>
  state.limsOos.items.filter((x) => x.state === "closed" && !!x.phase1)

// SPEC-022 (K10) worklist: open investigations that have reached the "qa-review" state — i.e. the
// subset QA must DECIDE on. The QA Analysis Review screen lists only these (the full team list of
// open investigations lives on the dedicated Investigations menu screen).
export const selectQaReviewInvestigations = (state) =>
  state.limsOos.items.filter((x) => x.state === "qa-review" && !!x.phase1)
